"use client"

import { ResizeObserver } from "@juggle/resize-observer"
import clsx from "clsx"
// eslint-disable-next-line import/no-unresolved -- globalHistory object code works, unsure why import doesn't resolve
// import { screenSizes } from "../utils/tailwind-config"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useState, useEffect, useContext, useRef } from "react"
import useDimensions from "react-cool-dimensions"

import RippleLogoDark from "../../../public/assets/ripple-logo-dark.svg"
import RippleLogoLight from "../../../public/assets/ripple-logo-light.svg"

import AppContext from "./AppContext"
import Button from "./Button"
import NavDesktop from "./navigation/desktop/NavDesktop"
import Hamburger from "./navigation/Hamburger"
import NavMobile from "./navigation/mobile/NavMobile"
import NavigationData from "./navigation/NavigationData"
import PageThemeContext from "./page-theme/PageThemeContext"
import PageGrid from "./PageGrid"
import SearchTrigger from "./search/SearchTrigger"

const Header: React.FC = () => {
  const [isDesktop, setIsDesktop] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const [navData] = useState(NavigationData)
  const [activeMenuName, setActiveMenuName] = useState<string>()
  const [allowHover, setAllowHover] = useState(true)
  const appContext = useContext(AppContext)
  const context = useRef(appContext)
  const pageThemeContext = useContext(PageThemeContext)
  const pathname = usePathname()

  // Add safety check for pageThemeContext with default fallback
  const safePageTheme = pageThemeContext?.pageTheme || {
    location: "/",
    dark: true,
    bgColorClass: "bg-gray-90",
    bgColorClassFooter: "bg-gray-90",
    bgColorHex: "#141a1f",
    bgColorHexFooter: "#141a1f",
  }
  // detect when there is a route / page change so that we can close the nav
  // reference for implementation here -
  // https://stackoverflow.com/questions/61274365/allow-component-to-detect-route-change-in-gatsby

  // Update the context ref whenever appContext changes
  useEffect(() => {
    if (appContext) {
      context.current = appContext
    }
  }, [appContext])

  useEffect(() => {
    // Function to be called when the route changes
    const handleRouteChange = () => {
      setNavOpen(false)
      setAllowHover(false)
      if (context.current && context.current.closeSearch) {
        context.current.closeSearch()
      }
    }

    // Optionally re-enable hover after a short delay
    setTimeout(() => {
      setNavOpen(false)
      setAllowHover(true)
    }, 300)

    handleRouteChange()
  }, [pathname])

  // The "currentBreakpoint" will be the object key based on the target's width
  // for instance, 0px - 1024px (currentBreakpoint = ZERO), note LG is intentionally set to 1025 (tailwind lg + 1)
  const { observe, height: headerHeight } = useDimensions({
    breakpoints: { ZERO: 0, LG: 1024 }, // screenSizes.lg + 1
    onResize: ({ currentBreakpoint }) =>
      currentBreakpoint !== "ZERO" ? setIsDesktop(true) : setIsDesktop(false),
    useBorderBoxSize: true,
    polyfill: ResizeObserver,
  })

  // this useEffect will reset navOpen when isDesktop changes
  useEffect(() => {
    // Close the nav when screen size changes
    setNavOpen(false)
  }, [isDesktop])

  useEffect(() => {
    if (!isDesktop && navOpen) {
      // Add the class to lock scrolling when nav is open and not on desktop
      document.body.classList.add("scroll-lock")
    } else {
      // Remove the class to enable scrolling when nav is closed or on desktop
      document.body.classList.remove("scroll-lock")
    }
  }, [isDesktop, navOpen])

  return (
    <header
      id="site-header"
      ref={observe}
      className={clsx(
        "z-50 w-full",
        isDesktop && "relative",
        !isDesktop && navOpen && "sticky left-0 top-0",
        safePageTheme.bgColorClass,
      )}
      role="banner"
    >
      <PageGrid
        isGrid={false}
        className={clsx(
          "items-center justify-between py-6 lg:py-0 relative z-50",
          safePageTheme.bgColorClass,
        )}
      >
        <div className="flex-grow lg:flex-none w-auto ml-4">
          <Link href="/" aria-label="Home">
            {safePageTheme.dark ? (
              <RippleLogoLight
                title="Link to Home, Ripple`s Company Logo"
                height="24"
                width="94"
                className="h-6 w-auto"
              />
            ) : (
              <RippleLogoDark
                title="Link to Home, Ripple`s Company Logo"
                height="24"
                width="94"
                className="h-6 w-auto"
              />
            )}
          </Link>
        </div>
        <div className="flex order-last w-auto ml-5 lg:order-none lg:justify-center lg:ml-0 relative">
          <NavDesktop
            navOpen={navOpen}
            navData={navData}
            allowHover={allowHover}
            setNavOpen={() => setNavOpen(navOpen)}
          />
          {!isDesktop && (
            <div className="lg:hidden block mt-2">
              <Hamburger
                navOpen={navOpen}
                hamburgerClick={() => {
                  setNavOpen(!navOpen)
                }}
              />
            </div>
          )}
        </div>
        <div className="flex w-auto lg:justify-end lg:mr-4 h-10">
          <Button
            to="/contact/sales"
            size="sm"
            className="mt-1 hidden lg:block whitespace-nowrap"
          >
            Contact Sales
          </Button>
          <SearchTrigger />
        </div>
      </PageGrid>
      {!isDesktop && (
        <NavMobile
          navOpen={navOpen}
          navData={navData}
          headerHeight={headerHeight}
          activeMenuName={activeMenuName}
          setActiveMenuName={setActiveMenuName}
        />
      )}
    </header>
  )
}

export default Header
